"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, Check, MailCheck } from "lucide-react";
import { Busy, ErrorNotice } from "@/components/ui";
import { AccountCard, StatusPill } from "@/components/account-card";
import { api, errorMessage, mutation } from "@/lib/api";

export function VerifyEmailPage() {
  const started = useRef(false);
  const [state, setState] = useState<"working" | "done" | "failed">("working");
  const [error, setError] = useState("");

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = new URLSearchParams(window.location.search).get("token") || "";
    window.history.replaceState(null, "", window.location.pathname);
    if (!token) {
      queueMicrotask(() => { setError("This verification link is missing or invalid."); setState("failed"); });
      return;
    }
    mutation("/auth/email-verification/confirm", { token })
      .then(() => setState("done"))
      .catch((cause) => { setError(errorMessage(cause)); setState("failed"); });
  }, []);

  return (
    <main className="sign-in">
      <div className="sign-in-form">
        <div className="sign-in-heading">
          <MailCheck size={22} />
          <h1>Email verification</h1>
        </div>
        {state === "working" && <Busy />}
        {state === "done" && <p className="configuration-saved" role="status">Your email is verified.</p>}
        {state === "failed" && <ErrorNotice message={error} />}
        <Link className="button button-primary" href="/">
          Continue to Velnqo
          <ArrowRight size={17} />
        </Link>
      </div>
    </main>
  );
}

type Status = { email: string; verified: boolean; canSend: boolean };

export function EmailVerification() {
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState(false);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const next = await api<Status>("/account/email-verification", { signal });
      if (!signal?.aborted) setStatus(next);
    } catch (err) {
      if (!signal?.aborted) setError(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => void load(controller.signal), 0);
    return () => { controller.abort(); window.clearTimeout(timer); };
  }, [load]);

  const resend = async () => {
    setPending(true);
    setError("");
    setNotice("");
    try {
      await mutation("/account/email-verification/resend", {});
      setNotice("Verification email sent. Check your inbox.");
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <AccountCard
      icon={<MailCheck size={18} />}
      title="Email verification"
      description="Verified email lets us reach you about billing and security."
      actions={status && <StatusPill tone={status.verified ? "good" : "warn"}>{status.verified ? "Verified" : "Not verified"}</StatusPill>}
    >
      <ErrorNotice message={error} />
      {notice && <p className="account-notice"><Check size={15} /> {notice}</p>}
      {!status ? <Busy /> : status.verified
        ? <p className="account-muted">{status.email} is verified.</p>
        : <div className="account-row">
            <span className="account-muted">{status.email} is not verified yet.</span>
            <button className="button button-primary" disabled={pending || !status.canSend} onClick={() => void resend()}>{pending ? "Sending..." : "Send verification email"}</button>
          </div>}
    </AccountCard>
  );
}
